import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles } from "lucide-react";

export default function WaitlistForm({ source = "landing" }) {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState({ phase: "idle", msg: "", position: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/waitlist/status");
        setStatus(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState({ phase: "loading", msg: "", position: null });
    try {
      const { data } = await api.post("/waitlist/join", { email, name: name || null, source });
      setState({
        phase: "success",
        msg: data.already_on_list ? "You're already on the list." : "You're in.",
        position: data.position,
      });
    } catch (e2) {
      setState({ phase: "error", msg: formatApiError(e2.response?.data?.detail) || e2.message, position: null });
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="waitlist-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-[#007AFF]" />
        <div className="text-xs uppercase tracking-widest text-[#007AFF]">Waitlist{status?.count ? ` · ${status.count} joined` : ""}</div>
      </div>
      <div className="font-bold text-lg">Get an early invite</div>
      <p className="text-neutral-400 text-sm mt-1 mb-4">
        Drop your email and we&apos;ll open the doors to you in the first cohort.
      </p>
      {state.phase === "success" ? (
        <div className="text-sm">
          <div className="text-[#34C759] font-semibold">{state.msg}</div>
          {state.position && (
            <div className="text-neutral-400 mt-1">
              You&apos;re number <span className="font-mono text-white">#{state.position}</span> in line.
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Input
            data-testid="waitlist-name"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-[#0A0A0A] border-[#27272A]"
          />
          <Input
            data-testid="waitlist-email"
            required
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-[#0A0A0A] border-[#27272A]"
          />
          {state.phase === "error" && (
            <div className="text-xs text-[#FF3B30]">{state.msg}</div>
          )}
          <Button
            data-testid="waitlist-submit"
            type="submit"
            disabled={state.phase === "loading"}
            className="w-full bg-white text-black hover:bg-neutral-200 rounded-full h-10 font-semibold"
          >
            {state.phase === "loading" ? "Joining…" : "Join the waitlist"}
          </Button>
        </form>
      )}
    </div>
  );
}
